import React, { useState, useEffect } from "react";
import { Location } from "history";
import { match as Match } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { injectIntl, IntlShape } from "react-intl";
import IntlMessages from "../../../../utils/messages";
import PageSubheader from "../../../../components/PageSubheader";
import Block from "../../../../components/Tailwind/Block";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import { withTwoColLayout } from "../../../../utils";
import overrideClasses from "../../../../utils/overrideClasses";
import { displayDayDDMMYYYY_HHMM } from "../../../../utils/config";
import GridQuoteItems from "../../../../components/Tailwind/GridQuoteItems";
import DropdownQuoteStatus from "../../../../components/Tailwind/DropdownQuoteStatus";
import { getFormattedPrice } from "../../../../utils/price-unit";

interface Props {
  match: Match<{ cid: string; id: string }>;
  intl: IntlShape;
  backURL: string;
  location: Location;
}

const Quote = (props: Props) => {
  const [loading, setLoading] = useState(false);
  const { intl, match, backURL, location } = props;

  const getQuote = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quote.getQuote
  );

  const quote = GroundGraphqlContextStore.useStoreState(
    state => state.quote.quote
  );

  const setQuote = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quote.setQuote
  );

  const center = GroundGraphqlContextStore.useStoreState(
    state => state.center.center
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([getQuote({ id: match.params.id })]).finally(() =>
      setLoading(false)
    );

    return () => {
      setQuote(null);
    };
  }, []);

  return !quote ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <>
        <Helmet>
          <title>
            {intl.formatMessage({
              id: "page.detail.quote.document.title",
            })}
          </title>
        </Helmet>

        <PageSubheader
          goBackEnabled
          goBackId="btn-back-quote"
          goBackURL={backURL}
          location={location}
          title="page.detail.quote.title"
        />

        <div className={overrideClasses("px-8 pb-32", { hidden: loading })}>
          <Block>
            <div className="py-3 sm:py-5 border-b border-gray-200 px-2 sm:px-6 flex flex-col">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center">
                <div className="mt-1">
                  <div className="text-17px text-neutral-900 leading-5">
                    <IntlMessages
                      id="page.detail.quote.number"
                      values={{ ref: quote?.ref }}
                    />
                  </div>
                  {quote?.created_at && (
                    <div className="text-14px text-ground-gray-100 leading-relaxed">
                      {displayDayDDMMYYYY_HHMM(
                        quote.created_at,
                        centerTimezone
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-1 sm:w-1/6 justify-end">
                  {quote && quote.status && (
                    <div className="flex flex-row items-center space-x-2">
                      <DropdownQuoteStatus
                        center={center}
                        quote={quote}
                        quoteStatus={[...quote.next_status, quote.status]}
                        intl={intl}
                        className="items-start"
                        fromQuoteDetails={true}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="py-3 sm:py-5 bg-gray-100 px-2 sm:px-6 flex flex-col sm:flex-row justify-between sm:items-start">
              <div className="flex flex-col text-14px leading-relaxed">
                <div className="flex flex-col">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="general.client" />
                  </p>
                  <p className="text-ground-gray-100">
                    {quote?.user?.first_name} {quote?.user?.last_name}
                  </p>
                  <p className="text-ground-blue-100">{quote?.user?.email}</p>
                </div>
                <div className="flex flex-col mt-4">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="general.expires_at" />
                  </p>
                  <p className=" text-ground-gray-100">
                    {displayDayDDMMYYYY_HHMM(quote.expires_at, centerTimezone)}
                  </p>
                </div>
              </div>

              <div>
                <div className="flex flex-col text-14px leading-relaxed text-ground-gray-100" />
                <div className="flex flex-col text-14px leading-relaxed text-ground-gray-100">
                  <p className="text-neutral-900 font-semibold">
                    <IntlMessages id="page.order.summary" />
                  </p>
                  <p>
                    <IntlMessages id="general.total.ht" /> :{" "}
                    {getFormattedPrice(quote.total_price, quote.currency)}
                  </p>
                  <p>
                    <IntlMessages id="general.vat" /> :{" "}
                    {getFormattedPrice(quote.total_tax, quote.currency)}
                  </p>
                  <p>
                    <IntlMessages id="general.total.ttc" /> :{" "}
                    {getFormattedPrice(quote.total_price_tax, quote.currency)}
                  </p>
                </div>
              </div>
            </div>
          </Block>

          <GridQuoteItems center={center} quote={quote} />
        </div>
      </>
    </>
  );
};

export default withTwoColLayout(injectIntl(Quote));
